import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  FaPlay,
  FaPause,
  FaDownload,
  FaChrome,
  FaSpinner,
  FaTimes,
  FaExpand,
  FaCompress,
  FaLink,
} from "react-icons/fa";
import { RiVideoLine, RiDeleteBin6Line } from "react-icons/ri";
import { extensionConfig } from "../../utils/authConfig";
import {
  getClientAccessUri,
  getVideos,
  deleteVideos,
  VideoWebSocketManager,
} from "../../utils/API_SERVICE_VIDEO";
import { useAuth } from "../../Hooks/useAuth";
import { useStudyId } from "../../Hooks/useStudyId";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { useTranslation } from "react-i18next";
import { replaceVideoUrlDomain } from "../../utils/helper";

// Toggleable debug logs for extension integration
const DEBUG_LOGS = true;

const ZikaronVideoRecording = () => {
  const { t } = useTranslation("zikaron");
  const [videos, setVideos] = useState([]);
  const [activeTab, setActiveTab] = useState("recorded"); // "recorded" or "intro"
  const [hasNewIntroVideo, setHasNewIntroVideo] = useState(false);
  const [recordingState, setRecordingState] = useState({
    isRecording: false,
    isPaused: false,
    isProcessing: false,
    isStarting: false,
    isLoading: false,
    error: null,
  });
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wsConnectionStatus, setWsConnectionStatus] = useState("disconnected");
  const [
    isExtensionSectionDismissed,
    setIsExtensionSectionDismissed,
  ] = useState(false);
  const [wsManager] = useState(() => new VideoWebSocketManager());
  const [processedVideoIds, setProcessedVideoIds] = useState(new Set());

  const videoRef = useRef(null);
  const recordingStateRef = useRef(null);
  const lastActionRef = useRef(null); // "start" | "stop" | null
  // Login retry state: timer and retry count
  const loginStateRef = useRef({
    timer: null,
    retryCount: 0,
  });
  const accessToken = useAuth();
  const studyId = useStudyId();
  const { isWixAuth, isCreAuth, wixUser, creUser, radicalUser } = usePatientAuthFlags();

  // Allowed projects for enabling recording
  const isProjectAllowed = useCallback((projectName) => {
    return (
      projectName === "report-engine" || projectName === "test-report-engine-local"
    );
  }, []);

  // Determine project name based on environment
  const getProjectName = useCallback(() => {
    const hostname = window.location.hostname;
    // Use "test-report-engine-local" for localhost/127.0.0.1, otherwise "report-engine"
    return hostname === "localhost" || hostname === "127.0.0.1"
      ? "test-report-engine-local"
      : "report-engine";
  }, []);

  // Send message to extension (matches PAGE_API.md format)
  const postToExtension = useCallback((eventName, payload = null) => {
    try {
      const message = {
        channel: "page",
        event: eventName,
      };
      // Add payload if provided
      if (payload !== null) {
        message.payload = payload;
      }
      window.postMessage(message, "*");
    } catch (e) {
      // no-op
    }
  }, []);

  // Send login request to extension with delay to ensure extension is ready
  const sendLoginRequest = useCallback(
    (delayMs = 500) => {
      const state = loginStateRef.current;
      // Clear any existing retry timer
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
      }

      state.timer = setTimeout(() => {
        const projectName = getProjectName();
        console.log(
          `[Zikaron] Sending login.request for project: ${projectName} (attempt ${state.retryCount +
            1})`
        );
        postToExtension("login.request", { project: projectName });
        state.timer = null;
      }, delayMs);
    },
    [postToExtension, getProjectName]
  );

  // Check extension status on mount
  useEffect(() => {
    // Check extension status on mount
    postToExtension("project.get");
    postToExtension("recording.state.get");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Keep a ref in sync with the latest recordingState
  useEffect(() => {
    recordingStateRef.current = recordingState;
  }, [recordingState]);

  // Listen for extension responses and push events (matches PAGE_API.md format)
  useEffect(() => {
    const handler = (event) => {
      const data = event?.data;
      if (!data || data.channel !== "page") return;
      if (DEBUG_LOGS) console.log("[Zikaron] Message received:", data);

        // Handle response events (type: "response")
      if (data.type === "response") {
        const { event: eventName, success, error, errorCode, payload } = data;

        // Handle project.get response
        if (eventName === "project.get") {
          const project = payload?.project;
          if (DEBUG_LOGS) console.log("[Zikaron] project.get response:", project);

          // If project is null/undefined, extension is installed but not logged in
          if (project === undefined || project === null) {
            // Extension is installed but not logged in - send login request with delay
            setIsExtensionInstalled(false);
            loginStateRef.current.retryCount = 0; // Reset retry count
            sendLoginRequest(1000); // Wait 1 second for extension to be ready
          } else if (isProjectAllowed(project)) {
            // Extension is installed and logged in - update UI immediately
            setIsExtensionInstalled(true);
          } else {
            // Project is not allowed
            setIsExtensionInstalled(false);
          }
        }

        // Handle login.request response
        if (eventName === "login.request") {
          const state = loginStateRef.current;
          if (DEBUG_LOGS) {
            console.log("[Zikaron] Login response detected:", {
              event: eventName,
              success,
              payload,
            });
          }

          if (success === true) {
            const sentProject = getProjectName();
            // Payload is a string with the project name (per API docs)
            const receivedProject = payload;

            if (receivedProject && receivedProject === sentProject) {
              console.log(
                "[Zikaron] ✅ Login successful - project matches:",
                receivedProject
              );
              // Update UI immediately - extension is now ready
              setIsExtensionInstalled(true);
              state.retryCount = 0; // Reset retry count on success
            } else if (
              receivedProject === null ||
              receivedProject === undefined
            ) {
              // Extension returned project: null - might not be ready yet, retry
              const maxRetries = 3;
              if (state.retryCount < maxRetries) {
                state.retryCount += 1;
                console.log(
                  `[Zikaron] ⚠️ Login returned project: null, retrying... (${state.retryCount}/${maxRetries})`
                );
                // Retry with increasing delay
                sendLoginRequest(2000 * state.retryCount);
              } else {
                console.warn(
                  "[Zikaron] ⚠️ Login failed after max retries - extension may need to be opened manually"
                );
                setIsExtensionInstalled(false);
                state.retryCount = 0;
              }
            } else {
              // Project doesn't match - not allowed
              console.warn(
                `[Zikaron] ⚠️ Login returned different project: ${receivedProject}, expected: ${sentProject}`
              );
              setIsExtensionInstalled(false);
              state.retryCount = 0;
            }
          } else if (success === false || error) {
            // Login failed
            console.error(
              "[Zikaron] ❌ Login error:",
              error || "Login failed",
              errorCode ? `(Code: ${errorCode})` : ""
            );
            setIsExtensionInstalled(false);
            state.retryCount = 0;
            
            // Handle specific error codes
            if (errorCode === "AUTH_LOGIN_FAILED") {
              toast.error(t("loginFailed") || error || "Login failed or cancelled");
            } else if (errorCode === "NET_OFFLINE") {
              toast.error(t("noInternetConnection") || "No internet connection. Please check your network.");
            } else {
              toast.error(error || "Login failed");
            }
          }
        }

        // Handle recording.state.get response
        if (eventName === "recording.state.get") {
          // Structure: data.payload.recordingState
          // Handles all state flags: isRecording, isPaused, isProcessing, isStarting, isLoading, error
          // IMPORTANT: Only update state here - do NOT show toast errors
          // Errors are only shown from explicit error responses (recording.start/stop with success: false)
          const rs = payload?.recordingState;
          if (rs) {
            if (DEBUG_LOGS) console.log("[Zikaron] recording.state:", rs);
            setRecordingState((prev) => ({
              ...prev,
              ...rs,
              error: rs.error || null,
            }));
          }
        }

        // Handle recording.start response
        if (eventName === "recording.start") {
          if (DEBUG_LOGS)
            console.log("[Zikaron] recording.start response:", data);
          if (success === true) {
            // Success acknowledgment - query state once, then rely on extension to send updates
            // Extension will send state updates via recording.state.get responses or push events
            if (DEBUG_LOGS)
              console.log("[Zikaron] Start acknowledged, querying state once");
            // Small delay to allow extension to update internal state before querying
            setTimeout(() => {
              postToExtension("recording.state.get");
            }, 100);
          } else if (success === false || error) {
            // Start failed - ONLY show error here because it comes explicitly from extension response
            const errorMessage = error || "Failed to start recording";
            console.warn("[Zikaron] Recording start error:", errorMessage, errorCode ? `(Code: ${errorCode})` : "");
            
            // Handle specific error codes
            let displayMessage = errorMessage;
            if (errorCode === "REC_NO_PERMISSIONS") {
              displayMessage = t("permissionDenied") || "Microphone or screen permission denied. Please grant permissions and try again.";
            } else if (errorCode === "REC_START_FAILED") {
              displayMessage = t("recordingStartFailed") || "Failed to initialize recording. Please try again.";
            } else if (errorCode === "AUTH_TOKEN_EXPIRED" || errorCode === "API_UNAUTHORIZED") {
              displayMessage = t("authenticationExpired") || "Authentication expired. Please log in again.";
              setIsExtensionInstalled(false);
              loginStateRef.current.retryCount = 0;
              sendLoginRequest(1000);
            } else if (errorCode === "NET_OFFLINE") {
              displayMessage = t("noInternetConnection") || "No internet connection. Please check your network.";
            }
            
            setRecordingState({
              isRecording: false,
              isPaused: false,
              isProcessing: false,
              isStarting: false,
              isLoading: false,
              error: displayMessage,
            });
            lastActionRef.current = null;
            // IMPORTANT: Only show toast error here - this is the ONLY place for recording.start errors
            toast.error(displayMessage);
          }
        }

        // Handle recording.stop response
        if (eventName === "recording.stop") {
          if (DEBUG_LOGS) console.log("[Zikaron] recording.stop response:", data);
          if (success === true) {
            // Success acknowledgment - recording.stopped push event will arrive and trigger state.get
            // No need to query state here - rely on push event
            if (DEBUG_LOGS)
              console.log("[Zikaron] Stop acknowledged, waiting for recording.stopped push event");
          } else if (success === false || error) {
            // Stop failed - ONLY show error here because it comes explicitly from extension response
            const errorMessage = error || "Failed to stop recording";
            console.warn("[Zikaron] Recording stop error:", errorMessage, errorCode ? `(Code: ${errorCode})` : "");
            
            // Handle specific error codes
            let displayMessage = errorMessage;
            if (errorCode === "AUTH_TOKEN_EXPIRED" || errorCode === "API_UNAUTHORIZED") {
              displayMessage = t("authenticationExpired") || "Authentication expired. Please log in again.";
              setIsExtensionInstalled(false);
              loginStateRef.current.retryCount = 0;
              sendLoginRequest(1000);
            } else if (errorCode === "API_SERVER_ERROR") {
              displayMessage = t("serverError") || "Server error occurred. Please try again later.";
            } else if (errorCode === "NET_OFFLINE") {
              displayMessage = t("noInternetConnection") || "No internet connection. Please check your network.";
            }
            
            setRecordingState({
              isRecording: false,
              isPaused: false,
              isProcessing: false,
              isStarting: false,
              isLoading: false,
              error: displayMessage,
            });
            lastActionRef.current = null;
            // IMPORTANT: Only show toast error here - this is the ONLY place for recording.stop errors
            toast.error(displayMessage);
          }
        }

        // Handle record.introductory.video response
        if (eventName === "record.introductory.video") {
          if (DEBUG_LOGS)
            console.log("[Zikaron] intro video response:", data);
          if (success) {
            toast.success("Intro recorder opened");
          } else if (error) {
            toast.error(error);
          }
        }
      }

      // Handle push events (type: "push")
      if (data.type === "push") {
        const { event: eventName, payload } = data;

        // Handle recording.stopped push event
        if (eventName === "recording.stopped") {
          if (DEBUG_LOGS) console.log("[Zikaron] recording.stopped push event");
          // Recording has been stopped, processing has begun
          // Query state to get updated status
          // recording.complete push event will arrive when processing finishes
          postToExtension("recording.state.get");
        }

        // Handle recording.complete push event
        if (eventName === "recording.complete") {
          if (DEBUG_LOGS) console.log("[Zikaron] recording.complete push event:", payload);
          // Video has been fully processed and is ready
          // The WebSocket will also send VIDEO_UPLOAD_COMPLETE_EVENT, so we can just log here
          // or refresh the video list
          if (payload?.vid) {
            console.log(
              `[Zikaron] ✅ Recording complete - Video ID: ${payload.vid}, Study ID: ${payload.studyId}`
            );
            // Optionally refresh video list or show notification
            // The WebSocket handler will add the video to the list
          }
          // Reset recording state
          setRecordingState((prev) => ({
            ...prev,
            isRecording: false,
            isProcessing: false,
            isStarting: false,
            isLoading: false,
            error: null,
          }));
        }

        // Handle recording.failed push event
        if (eventName === "recording.failed") {
          if (DEBUG_LOGS) console.log("[Zikaron] recording.failed push event:", payload);
          const errorMessage = payload?.error || "Recording failed";
          const errorCode = payload?.code;
          
          // Handle specific error codes
          if (errorCode === "AUTH_TOKEN_EXPIRED" || errorCode === "API_UNAUTHORIZED") {
            // Token expired - user needs to re-login
            console.warn("[Zikaron] ⚠️ Authentication expired, requesting re-login");
            setIsExtensionInstalled(false);
            loginStateRef.current.retryCount = 0;
            sendLoginRequest(1000);
            toast.error(t("authenticationExpired") || "Authentication expired. Please log in again.");
          } else if (errorCode === "API_RATE_LIMIT") {
            toast.error(t("rateLimitExceeded") || "Too many requests. Please try again later.");
          } else if (errorCode === "NET_OFFLINE") {
            toast.error(t("noInternetConnection") || "No internet connection. Please check your network.");
          } else if (errorCode === "API_SERVER_ERROR") {
            toast.error(t("serverError") || "Server error occurred. Please try again later.");
          } else {
            // Generic error handling
            toast.error(errorMessage);
          }
          
          // Reset recording state
          setRecordingState((prev) => ({
            ...prev,
            isRecording: false,
            isProcessing: false,
            isStarting: false,
            isLoading: false,
            error: errorMessage,
          }));
          lastActionRef.current = null;
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [isProjectAllowed, postToExtension, sendLoginRequest, getProjectName, t]);

  // Optional: re-check on focus only (no periodic polling)
  useEffect(() => {
    if (isExtensionInstalled || isExtensionSectionDismissed) return;
    const onFocus = () => {
      // Re-check extension status when user returns to page
      postToExtension("project.get");
      // Also retry login if extension might be ready now
      loginStateRef.current.retryCount = 0; // Reset retry count
      sendLoginRequest(500);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [
    isExtensionInstalled,
    isExtensionSectionDismissed,
    postToExtension,
    sendLoginRequest,
  ]);

  // Clear login retry timer on unmount
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const loginState = loginStateRef.current;
      if (loginState?.timer) {
        clearTimeout(loginState.timer);
        loginState.timer = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartRecording = useCallback(async () => {
    // Gate by project via installed flag (set by project.get)
    if (!isExtensionInstalled) {
      toast.info(t("installToStartRecording"));
      return;
    }
    if (recordingState.isStarting || recordingState.isProcessing) return;
    try {
      const newState = { ...recordingState, isStarting: true, error: null };
      setRecordingState(newState);
      recordingStateRef.current = newState;
      if (DEBUG_LOGS) console.log("[Zikaron] starting recording...");
      lastActionRef.current = "start";
      postToExtension("recording.start");
      // Extension will respond with recording.start response, which triggers recording.state.get
      // State updates will come via recording.state.get response handler
      // No polling needed - rely on extension events only
    } catch (err) {
      // Only extension response handlers show errors - don't show errors here
      console.warn("[Zikaron] Start recording error:", err.message);
      setRecordingState((prev) => ({
        ...prev,
        isStarting: false,
      }));
      lastActionRef.current = null;
    } finally {
      // Do not forcibly clear isStarting; let extension state drive UI
    }
  }, [
    isExtensionInstalled,
    postToExtension,
    recordingState,
    t,
  ]);

  const handleStopRecording = useCallback(async () => {
    if (!isExtensionInstalled) return;
    if (recordingState.isLoading || recordingState.isProcessing) return;
    try {
      const newState = {
        ...recordingState,
        isLoading: true,
        isStarting: false,
        error: null,
      };
      setRecordingState(newState);
      recordingStateRef.current = newState;
      if (DEBUG_LOGS) console.log("[Zikaron] stopping recording...");
      lastActionRef.current = "stop";
      postToExtension("recording.stop");
      // Extension will respond with recording.stop response, which triggers recording.state.get
      // State updates will come via recording.state.get response handler
      // Processing completion will be handled by recording.complete push event
      // No polling needed - rely on extension events only
    } catch (err) {
      // Only extension response handlers show errors - don't show errors here
      console.warn("[Zikaron] Stop recording error:", err.message);
      setRecordingState((prev) => ({
        ...prev,
        isLoading: false,
      }));
      lastActionRef.current = null;
    } finally {
      // Do not forcibly clear isLoading; let extension state drive UI
    }
  }, [
    isExtensionInstalled,
    postToExtension,
    recordingState,
  ]);

  const handleOpenIntroRecorder = useCallback(() => {
    if (!isExtensionInstalled) {
      toast.info(t("installToOpenIntroRecorder"));
      return;
    }
    postToExtension("record.introductory.video");
  }, [isExtensionInstalled, postToExtension, t]);

  // Handle new video received via WebSocket
  const handleNewVideo = useCallback(
    (messageData) => {
      console.log("New video received:", messageData);

      // Check if this is a VIDEO_UPLOAD_COMPLETE_EVENT
      if (
        messageData.MessageType === "VIDEO_UPLOAD_COMPLETE_EVENT" &&
        messageData.Payload
      ) {
        const payload = messageData.Payload;

        // Get current user ID — Wix, CRE, and Radical each store the user under a different key.
        const currentUserId = isWixAuth
          ? wixUser?.userId
          : isCreAuth
            ? creUser?.userId
            : radicalUser?.userId;

        // Validate that this video is for the current user
        const isForCurrentUser =
          payload.UserID && currentUserId && payload.UserID === currentUserId;

        // Check if it's a recorded video (has StudyId) or intro video (no StudyId)
        const isIntroVideo = !payload.StudyId || payload.StudyId === "";
        const isForCurrentStudy =
          !isIntroVideo &&
          payload.StudyId &&
          studyId &&
          payload.StudyId === studyId;

        console.log("Video validation:", {
          payloadStudyId: payload.StudyId,
          currentStudyId: studyId,
          payloadUserId: payload.UserID,
          currentUserId: currentUserId,
          isForCurrentUser,
          isForCurrentStudy,
          isIntroVideo,
        });

        // Accept video if: (recorded video for current study) OR (intro video for current user)
        if (isForCurrentUser && (isForCurrentStudy || isIntroVideo)) {
          // Early return checks (using ref for synchronous check to prevent race conditions)
          if (processedVideoIds.has(payload.Vid)) {
            console.log(
              `Video ${payload.Vid} already processed, skipping duplicate`
            );
            return;
          }

          // Also check if video already exists in current videos array (race condition protection)
          // For intro videos, we allow replacing old intro videos, but skip if same ID exists
          // For recorded videos, skip if same ID exists
          const videoAlreadyExists = videos.some(
            (video) => video.vid === payload.Vid
          );
          if (videoAlreadyExists) {
            console.log(
              `Video ${payload.Vid} already exists in videos array, skipping duplicate`
            );
            // Mark as processed to prevent future duplicates
            setProcessedVideoIds((prev) => new Set([...prev, payload.Vid]));
            return;
          }
          
          // For intro videos: if there's an existing intro video with different ID, we'll replace it
          // This is handled in setVideos callback below

          // Mark this video as processed BEFORE updating state to prevent race conditions
          setProcessedVideoIds((prev) => new Set([...prev, payload.Vid]));

          // Create new video object
          const newVideo = {
            id: payload.Vid || Date.now().toString(),
            name: isIntroVideo
              ? `Intro Video ${payload.Vid || "New"}`
              : `Video ${payload.Vid || "New"}`,
            url: replaceVideoUrlDomain(payload.ReadSasUrl), // Replace domain before storing
            size: 0, // Size not provided in WebSocket message
            duration: 0, // Will be set when video loads
            date: payload.CompletedAt || new Date().toISOString(),
            vid: payload.Vid,
            streamId: payload.StreamId,
            status: payload.Status,
            processingDuration: payload.ProcessingDuration,
            studyId: payload.StudyId,
            userId: payload.UserID,
            isIntro: isIntroVideo,
            isNew: true, // Flag for videos from WebSocket
          };

          // Determine if we should show badge
          const shouldShowBadge = isIntroVideo && activeTab !== "intro";

          // Track if video was actually added (for recorded videos only)
          let wasNewVideo = false;
          setVideos((prev) => {
            // For intro videos, replace existing intro video instead of appending
            if (isIntroVideo) {
              // Remove any existing intro videos
              const filteredVideos = prev.filter((video) => !video.isIntro);
              console.log(`Replacing intro video with new one: ${payload.Vid}`);
              wasNewVideo = true; // Intro videos are always "new" when replacing
              return [newVideo, ...filteredVideos];
            } else {
              // For recorded videos, check if it already exists
              const videoExists = prev.some(
                (video) => video.vid === payload.Vid
              );
              if (videoExists) {
                console.log(
                  `Video ${payload.Vid} already exists in list, skipping duplicate`
                );
                wasNewVideo = false;
                return prev;
              }
              console.log(`Adding new recorded video: ${payload.Vid}`);
              wasNewVideo = true;
              return [newVideo, ...prev];
            }
          });

          // Set badge for new intro videos
          if (wasNewVideo && shouldShowBadge) {
            setHasNewIntroVideo(true);
          }

          // Upload completed → reset recording flags to idle
          setRecordingState((prev) => ({
            ...prev,
            isRecording: false,
            isProcessing: false,
            isStarting: false,
            isLoading: false,
            error: null,
          }));
        } else {
          console.log("Video not for current user/study, ignoring:", {
            payloadStudyId: payload.StudyId,
            currentStudyId: studyId,
            payloadUserId: payload.UserID,
            currentUserId: currentUserId,
            isIntroVideo,
          });
        }
      } else {
        console.log("Received non-video message:", messageData);
      }
    },
    [
      isWixAuth,
      isCreAuth,
      wixUser?.userId,
      creUser?.userId,
      radicalUser?.userId,
      studyId,
      processedVideoIds,
      activeTab,
      videos,
    ]
  );

  // Fetch existing videos
  const fetchVideos = useCallback(async () => {
    try {
      const response = await getVideos(studyId, accessToken);
      if (response.success && response.payload) {
        const formattedVideos = response.payload.map((video) => ({
          id: video.vid,
          name: video.isIntro
            ? `Intro Video ${video.vid}`
            : `Video ${video.vid}`,
          url: replaceVideoUrlDomain(video.longUrl), // Replace domain before storing
          size: 0, // Size not provided in API response
          duration: 0, // Will be set when video loads
          date: video.createdAt,
          vid: video.vid,
          isIntro: video.isIntro || false,
        }));
        setVideos(formattedVideos);
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast.error(t("failedToFetchVideos"));
    }
  }, [studyId, accessToken, t]);

  // Initialize video connection (WebSocket + fetch existing videos)
  const initializeVideoConnection = useCallback(async () => {
    try {
      setIsLoading(true);

      // Disconnect any existing connection first to prevent duplicates
      wsManager.disconnect();

      // Get WebSocket URL
      const wsResponse = await getClientAccessUri(accessToken);
      if (wsResponse.success && wsResponse.payload?.wssMessagesChannelUrl) {
        // Connect to WebSocket
        await wsManager.connect(
          wsResponse.payload.wssMessagesChannelUrl,
          handleNewVideo,
          setWsConnectionStatus
        );
        setIsLoading(false);
      }

      // Fetch existing videos
      await fetchVideos();
    } catch (error) {
      console.error("Error initializing video connection:", error);
      toast.error("Failed to initialize video connection");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, wsManager, fetchVideos, handleNewVideo]);

  // Initialize WebSocket connection and fetch videos
  useEffect(() => {
    if (accessToken && studyId) {
      initializeVideoConnection();
    }

    // Cleanup function to disconnect WebSocket on unmount
    return () => {
      console.log("Cleaning up WebSocket connection");
      wsManager.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, studyId]); // Removed initializeVideoConnection and wsManager from dependencies

  // Additional cleanup effect for hot reloads
  useEffect(() => {
    return () => {
      // Cleanup on component unmount or hot reload
      wsManager.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removed wsManager from dependencies

  const handleInstallExtension = useCallback(() => {
    // Open the specific Zikaron extension page using browser-specific URL
    const extensionUrl = extensionConfig.getExtensionUrl();
    window.open(extensionUrl, "_blank");
    setShowInstallGuide(true);
    const storeName =
      window.navigator.userAgent.includes("Edg") ||
      window.navigator.userAgent.includes("Edge")
        ? t("edgeAddonsStore")
        : t("chromeWebStore");
    toast.info(t("installFromStore", { store: storeName }));
  }, [t]);

  const handlePlayPause = useCallback(async () => {
    if (!videoRef.current) return;
    
    try {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        // play() returns a Promise - handle it properly to avoid errors
        await videoRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      // Handle play() errors (e.g., video was paused before play() completed)
      if (error.name !== 'NotAllowedError' && error.name !== 'AbortError') {
        console.error('Error playing/pausing video:', error);
      }
      // Sync state with actual video state
      setIsPlaying(!videoRef.current.paused);
    }
  }, [isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      // Sync playing state with actual video state
      setIsPlaying(!videoRef.current.paused);
    }
  }, []);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleSeek = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newTime = (clickX / width) * duration;

      if (videoRef.current) {
        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      }
    },
    [duration]
  );

  const handleDeleteVideo = useCallback(
    (videoId) => {
      console.log("🚀 ~ VideoRecording ~ videoId:", videoId);
      // Find the video to get its vid
      const videoToDelete = videos.find((video) => video.id === videoId);
      if (!videoToDelete) {
        toast.error(t("videoNotFound"));
        return;
      }

      const videoVid = videoToDelete.vid;

      if (!videoVid) {
        toast.error(t("videoIdNotFound"));
        return;
      }

      // Remove video from state immediately (optimistic update)
      setVideos((prev) => {
        const updatedVideos = prev.filter((video) => video.id !== videoId);
        if (selectedVideo && selectedVideo.id === videoId) {
          setSelectedVideo(null);
          setIsPlaying(false);
        }
        return updatedVideos;
      });
      toast.success(t("videoDeletedSuccessfully"));

      // Call API in the background (don't wait for response)
      const globalStudyId = videoToDelete.isIntro ? null : studyId || null;
      deleteVideos(globalStudyId, [videoVid], accessToken).catch((error) => {
        console.error("Error deleting video:", error);
        // Only show error if API call fails, but don't rollback
        toast.error(t("failedToDeleteVideoOnServer"));
      });
    },
    [selectedVideo, videos, accessToken, studyId, t]
  );

  const handleDownload = useCallback((video) => {
    const link = document.createElement("a");
    link.href = video.url;
    link.download = video.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleCopyVideoLink = useCallback(async (video) => {
    try {
      await navigator.clipboard.writeText(video.url);
      toast.success(t("videoLinkCopiedToClipboard"));
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = video.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(t("videoLinkCopiedToClipboard"));
    }
  }, [t]);

  const formatTime = useCallback((time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  const handleFullscreenToggle = useCallback(() => {
    if (!document.fullscreenElement) {
      videoRef.current?.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error("Error attempting to exit fullscreen:", err);
      });
    }
  }, []);

  const handleVideoSelect = useCallback((video) => {
    // Pause current video if playing (handle promise to avoid errors)
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause().catch(() => {
        // Ignore pause errors (video might already be paused or loading)
      });
    }
    setSelectedVideo(video);
    setIsPlaying(false);
    setCurrentTime(0);

    // Remove isNew flag when video is selected
    if (video.isNew) {
      setVideos((prev) =>
        prev.map((v) => (v.vid === video.vid ? { ...v, isNew: false } : v))
      );
      // Clear "NEW" badge on intro tab when an intro video is selected
      if (video.isIntro) {
        setHasNewIntroVideo(false);
      }
    }
  }, []);

  // Clear selected video when switching tabs if it doesn't belong to the active tab
  // Also clear "NEW" badge when switching to a tab
  useEffect(() => {
    if (selectedVideo) {
      const isSelectedVideoIntro = selectedVideo.isIntro || false;
      const shouldBeVisible =
        (activeTab === "intro" && isSelectedVideoIntro) ||
        (activeTab === "recorded" && !isSelectedVideoIntro);

      if (!shouldBeVisible) {
        setSelectedVideo(null);
        setIsPlaying(false);
        setCurrentTime(0);
      }
    }
    // Clear "NEW" badge when switching to intro tab
    if (activeTab === "intro") {
      setHasNewIntroVideo(false);
    }
  }, [activeTab, selectedVideo]);

  return (
    <div className="mt-6 p-6 border dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t("videoRecordingTitle")}
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              {t("videoRecordingDescription")}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {isLoading && (
              <FaSpinner className="animate-spin text-[#A4C639] dark:text-[#CBEA7B]" />
            )}
            <div
              className={`px-3 py-1 rounded-full text-sm ${
                wsConnectionStatus === "connected"
                  ? "bg-[#CBEA7B] text-gray-800 dark:bg-[#A4C63980] dark:text-gray-200"
                  : wsConnectionStatus === "connecting"
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
              }`}
            >
              {wsConnectionStatus === "connected"
                ? t("connected")
                : wsConnectionStatus === "connecting"
                ? t("connecting")
                : t("disconnected")}
            </div>
          </div>
        </div>
      </div>

      {/* Extension Installation Section - only when not installed */}
      {!isExtensionInstalled && !isExtensionSectionDismissed && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-[#111827] rounded-lg border-2 border-gray-200 dark:border-[#111827] relative shadow-sm">
          {/* Close button positioned at top-right corner */}
          <button
            onClick={() => setIsExtensionSectionDismissed(true)}
            className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            title={t("dismiss")}
          >
            <FaTimes className="text-sm" />
          </button>

          <div className="flex items-center justify-between pr-8">
            <div className="flex items-center space-x-3">
              <FaChrome className="text-gray-600 dark:text-white text-2xl" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {t("extensionTitle")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t("extensionDescription")}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t("extensionLoggedIn")}{" "}
                  <button
                    onClick={() => postToExtension("project.get")}
                    className="ml-2 text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {t("refreshStatus")}
                  </button>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleInstallExtension}
                className="px-4 py-2 bg-[#A4C639] dark:bg-[#A4C639] text-white dark:text-white rounded-lg hover:bg-[#CBEA7B80] dark:hover:bg-[#CBEA7B80] transition-colors"
              >
                {t("installExtension")}
              </button>
            </div>
          </div>

          {showInstallGuide &&
            (() => {
              const isEdgeBrowser =
                window.navigator.userAgent.includes("Edg") ||
                window.navigator.userAgent.includes("Edge");
              const installStep2 = isEdgeBrowser
                ? t("installStep2Edge")
                : t("installStep2Chrome");

              return (
                <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {t("installGuideTitle")}
                  </h4>
                  <ol className="list-decimal list-inside text-sm text-gray-800 dark:text-gray-300 space-y-1">
                    <li>{t("installStep1")}</li>
                    <li>{installStep2}</li>
                    <li>{t("installStep3")}</li>
                    <li>{t("installStep4")}</li>
                  </ol>
                  <div className="mt-2 text-xs text-gray-700 dark:text-gray-400">
                    {t("installFeatures")}
                  </div>
                </div>
              );
            })()}
        </div>
      )}

      {/* Recording Controls - only when extension is installed */}
      {isExtensionInstalled && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={
                recordingState.isRecording
                  ? handleStopRecording
                  : handleStartRecording
              }
              className={`px-4 py-2 rounded-lg text-white transition-colors ${
                recordingState.isRecording
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-[#A4C639] hover:bg-[#CBEA7B80] text-gray-900"
              }`}
              disabled={
                recordingState.isStarting ||
                recordingState.isProcessing ||
                recordingState.isLoading
              }
              title={
                recordingState.isRecording
                  ? t("stopRecording")
                  : t("launchRecording")
              }
            >
              {recordingState.isStarting
                ? t("starting")
                : recordingState.isLoading
                ? t("stopping")
                : recordingState.isProcessing
                ? t("processing")
                : recordingState.isRecording
                ? t("stopRecording")
                : t("launchRecording")}
            </button>
            <button
              onClick={handleOpenIntroRecorder}
              className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white transition-colors"
            >
              {t("openIntroRecorder")}
            </button>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {recordingState.isRecording
                ? t("recordingInProgress")
                : recordingState.isProcessing
                ? t("processingRecording")
                : t("ready")}
            </div>
          </div>
          {recordingState.error && (
            <div className="mt-2 text-sm text-red-600 dark:text-red-400">
              {recordingState.error}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video List */}
        <div className="lg:col-span-1">
          {/* Tabs */}
          <div className="flex items-center gap-2 mb-4 border-b border-gray-200 dark:border-gray-700">
            {(() => {
              // Calculate counts
              const recordedCount = videos.filter((v) => !v.isIntro).length;
              const introCount = videos.filter((v) => v.isIntro).length;

              return (
                <>
                  <button
                    onClick={() => setActiveTab("recorded")}
                    className={`relative px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                      activeTab === "recorded"
                        ? "text-[#A4C639] dark:text-[#CBEA7B] border-b-2 border-[#A4C639] dark:border-[#CBEA7B]"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    }`}
                  >
                    <span>{t("recordedVideos")}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        activeTab === "recorded"
                          ? "bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-gray-900"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {recordedCount}
                    </span>
                  </button>
                  <button
                    onClick={() => setActiveTab("intro")}
                    className={`relative px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                      activeTab === "intro"
                        ? "text-[#A4C639] dark:text-[#CBEA7B] border-b-2 border-[#A4C639] dark:border-[#CBEA7B]"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    }`}
                  >
                    <span>{t("introVideos")}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        activeTab === "intro"
                          ? "bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-gray-900"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {introCount}
                    </span>
                    {hasNewIntroVideo && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#A4C639] dark:bg-[#CBEA7B] border-2 border-white dark:border-gray-800 animate-pulse"></span>
                    )}
                  </button>
                </>
              );
            })()}
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(() => {
              // Filter videos based on active tab
              const filteredVideos = videos.filter((video) =>
                activeTab === "intro" ? video.isIntro : !video.isIntro
              );

              if (filteredVideos.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <RiVideoLine className="mx-auto text-4xl mb-2" />
                    <p>
                      {activeTab === "intro"
                        ? t("noIntroVideosYet")
                        : t("noRecordedVideosYet")}
                    </p>
                    <p className="text-sm">
                      {activeTab === "intro"
                        ? t("useIntroRecorderHint")
                        : t("useExtensionHint")}
                    </p>
                  </div>
                );
              }

              return filteredVideos.map((video) => (
                <div
                  key={video.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                    selectedVideo?.id === video.id
                      ? "bg-[#CBEA7B] dark:bg-[#A4C63980] border-[#A4C639] dark:border-[#CBEA7B] shadow-md"
                      : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-[#CBEA7B80] dark:hover:bg-[#A4C63980] hover:shadow-sm"
                  }`}
                  onClick={() => handleVideoSelect(video)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {video.name}
                        </p>
                        {video.isNew && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#A4C639] text-gray-800 dark:bg-[#CBEA7B] dark:text-gray-800 animate-pulse">
                            {t("common:new")}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {video.status === "completed"
                          ? `✅ ${t("completed")}`
                          : video.status}{" "}
                        • {new Date(video.date).toLocaleDateString()}
                      </p>
                      {video.processingDuration && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {t("processedIn")} {video.processingDuration}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(video);
                        }}
                        className="p-1 text-gray-500 hover:text-[#A4C639] dark:text-white dark:hover:text-[#CBEA7B]"
                        title={t("download")}
                      >
                        <FaDownload className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyVideoLink(video);
                        }}
                        className="p-1 text-gray-500 hover:text-[#A4C639] dark:text-white dark:hover:text-[#CBEA7B]"
                        title={t("copyVideoLink")}
                      >
                        <FaLink className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVideo(video.id);
                        }}
                        className="p-1 text-gray-500 hover:text-red-600 dark:text-white dark:hover:text-red-400"
                        title={t("delete")}
                      >
                        <RiDeleteBin6Line className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* Video Player */}
        <div className="lg:col-span-2">
          {selectedVideo ? (
            <div className="bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                src={selectedVideo.url}
                className="w-full h-64 lg:h-80 object-contain"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onEnded={handleVideoPause}
              />

              {/* Video Controls */}
              <div className="p-4 bg-gray-900 text-white">
                <div className="flex items-center space-x-4 mb-3">
                  <button
                    onClick={handlePlayPause}
                    className="flex items-center justify-center w-10 h-10 bg-[#CBEA7B] rounded-full hover:bg-[#A4C639] dark:bg-[#A4C639] dark:hover:bg-[#A4C63980] transition-colors"
                  >
                    {isPlaying ? <FaPause /> : <FaPlay />}
                  </button>

                  <div className="flex-1">
                    <div
                      className="w-full h-2 bg-gray-600 rounded-full cursor-pointer"
                      onClick={handleSeek}
                    >
                      <div
                        className="h-2 bg-[#A4C639] dark:bg-[#A4C639] rounded-full transition-all"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                      />
                    </div>
                  </div>

                  <span className="text-sm text-gray-300">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <h4 className="font-medium truncate">{selectedVideo.name}</h4>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDownload(selectedVideo)}
                      className="flex items-center justify-center w-8 h-8 text-gray-300 hover:text-[#A4C639] dark:hover:text-[#CBEA7B] transition-colors"
                      title={t("downloadVideo")}
                    >
                      <FaDownload className="text-sm" />
                    </button>
                    <button
                      onClick={() => handleCopyVideoLink(selectedVideo)}
                      className="flex items-center justify-center w-8 h-8 text-gray-300 hover:text-[#A4C639] dark:hover:text-[#CBEA7B] transition-colors"
                      title={t("copyVideoLink")}
                    >
                      <FaLink className="text-sm" />
                    </button>
                    <button
                      onClick={handleFullscreenToggle}
                      className="flex items-center justify-center w-8 h-8 text-gray-300 hover:text-[#A4C639] dark:hover:text-[#CBEA7B] transition-colors"
                      title={
                        document.fullscreenElement
                          ? t("exitFullscreen")
                          : t("enterFullscreen")
                      }
                    >
                      {document.fullscreenElement ? (
                        <FaCompress />
                      ) : (
                        <FaExpand />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-64 lg:min-h-80 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <RiVideoLine className="mx-auto text-6xl mb-4" />
                <p className="text-lg font-medium">{t("selectVideoToPlay")}</p>
                <p className="text-sm">
                  {t("chooseVideoFromList")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZikaronVideoRecording;
